// 全域 toast 通知（取代 alert / confirm 的失敗訊息）
//
// 用法：
//   import { toast } from '../lib/toast';
//   toast.success('已存好');
//   toast.error('存檔失敗：' + err.message);
//   toast.info('已複製連結');
//
// 顯示在右下角，滑入動畫 + 自動消失（success 3s / info 4s / error 6s）

import { useEffect, useState } from 'react';
import { VIEW_AS_BLOCKED_MESSAGE } from './viewAs';

export type ToastKind = 'success' | 'info' | 'warn' | 'error';

/** 提示上的動作按鈕 */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastItem {
  id: string;
  kind: ToastKind;
  text: string;
  durationMs: number;
  /** 提示上的動作按鈕（例如刪除後的「復原」）；按了先關掉提示再執行 */
  action?: ToastAction;
}

type Listener = (items: ToastItem[]) => void;

const DEFAULT_DURATIONS: Record<ToastKind, number> = {
  success: 3000,
  info: 4000,
  warn: 5000,
  error: 6000,
};

class ToastStore {
  items: ToastItem[] = [];
  private listeners = new Set<Listener>();
  private nextId = 1;

  push(kind: ToastKind, text: string, durationMs?: number, action?: ToastAction): string {
    const id = 't' + this.nextId++;
    const item: ToastItem = { id, kind, text, durationMs: durationMs ?? DEFAULT_DURATIONS[kind], ...(action ? { action } : {}) };
    this.items = [...this.items, item];
    this.notify();
    if (item.durationMs > 0) {
      setTimeout(() => this.dismiss(id), item.durationMs);
    }
    return id;
  }

  dismiss(id: string): void {
    this.items = this.items.filter(t => t.id !== id);
    this.notify();
  }

  success(text: string, durationMs?: number, action?: ToastAction) { return this.push('success', text, durationMs, action); }
  info(text: string, durationMs?: number)    { return this.push('info', text, durationMs); }
  warn(text: string, durationMs?: number)    { return text.includes(VIEW_AS_BLOCKED_MESSAGE) ? this.viewAsBlocked() : this.push('warn', text, durationMs); }
  // 預覽模式（用其他身分看）擋下的寫入不是壞掉：不管哪裡丟出來，都改成中性提示
  error(text: string, durationMs?: number)   { return text.includes(VIEW_AS_BLOCKED_MESSAGE) ? this.viewAsBlocked() : this.push('error', text, durationMs); }

  private lastBlockedAt = -Infinity;
  /** 預覽模式擋下寫入的中性提示（連點只說一次） */
  viewAsBlocked(): string {
    const now = Date.now();
    if (now - this.lastBlockedAt < 1500) return '';
    this.lastBlockedAt = now;
    return this.push('info', VIEW_AS_BLOCKED_MESSAGE);
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.items);
    return () => { this.listeners.delete(fn); };
  }

  private notify() {
    for (const l of this.listeners) l(this.items);
  }
}

export const toast = new ToastStore();

export function useToasts(): ToastItem[] {
  const [items, setItems] = useState<ToastItem[]>(toast.items);
  useEffect(() => toast.subscribe(setItems), []);
  return items;
}
